import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { fileUrl } from "../components/ImageUpload";
import { Send, ArrowLeft, MessageSquare, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function imgSrc(v, fallback) {
  if (!v) return fallback;
  if (v.startsWith("http") || v.startsWith("/")) return v;
  return `${BACKEND}/api/files/${v}`;
}

export default function Messages() {
  const { user } = useAuth();
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const listEndRef = useRef(null);

  const fetchConvs = async () => {
    const { data } = await api.get("/conversations/mine");
    setConvs(data.conversations || []);
    return data.conversations || [];
  };

  const fetchActive = async (id) => {
    const { data } = await api.get(`/conversations/${id}`);
    setActive(data.conversation);
    setMessages(data.messages || []);
  };

  useEffect(() => {
    (async () => {
      try {
        const list = await fetchConvs();
        if (conversationId) {
          await fetchActive(conversationId);
        } else if (list.length > 0) {
          navigate(`/messages/${list[0].id}`, { replace: true });
        }
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [conversationId]);

  useEffect(() => {
    // Poll every 5s for new messages on active conversation
    if (!conversationId) return;
    const t = setInterval(() => {
      fetchActive(conversationId).catch(() => {});
      fetchConvs().catch(() => {});
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [conversationId]);

  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim() || !active) return;
    setSending(true);
    try {
      await api.post(`/conversations/${active.id}/messages`, { body });
      setBody("");
      await fetchActive(active.id);
      await fetchConvs();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="container-mc py-20 text-center font-mono text-sm uppercase tracking-wider text-muted-foreground">Chargement…</div>;

  return (
    <div className="container-mc py-4 md:py-8" data-testid="messages-page">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 card-mc min-h-[80vh] md:min-h-[70vh]">
        {/* SIDEBAR — hidden on mobile when a thread is open */}
        <aside className={`lg:col-span-4 border-r border-border bg-card ${conversationId ? "hidden lg:block" : "block"}`}>
          <div className="p-5 border-b border-border">
            <p className="overline">Messagerie</p>
            <h1 className="font-display font-black text-2xl tracking-tighter">Conversations</h1>
          </div>
          {convs.length === 0 ? (
            <div className="p-8 text-center" data-testid="no-conversations">
              <MessageSquare className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-4">Aucune conversation pour le moment.</p>
              {user?.role === "client" && <Link to="/explore" className="btn-primary text-sm">Explorer les artistes</Link>}
            </div>
          ) : (
            <ul>
              {convs.map((c) => {
                const isActive = active?.id === c.id;
                const title = user?.role === "client" ? c.artist_stage_name : c.client_name;
                return (
                  <li key={c.id}>
                    <Link
                      to={`/messages/${c.id}`}
                      data-testid={`conv-${c.id}`}
                      className={`flex gap-3 p-4 border-b border-border transition-colors ${isActive ? "bg-muted" : "hover:bg-muted/50"}`}
                    >
                      <div className="w-12 h-12 bg-muted overflow-hidden flex-shrink-0">
                        {c.artist_cover && <img src={imgSrc(c.artist_cover)} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-display font-bold truncate">{title || "—"}</p>
                          {c.unread_count > 0 && (
                            <span className="bg-[hsl(var(--primary))] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-sm" data-testid={`unread-${c.id}`}>{c.unread_count}</span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">{c.last_message_preview || "Conversation ouverte"}</p>
                        {!c.unlock_paid && (
                          <p className="text-[10px] font-mono text-[hsl(var(--primary))] uppercase tracking-wider mt-1">⚠ Paiement en attente</p>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </aside>

        {/* THREAD */}
        <section className={`lg:col-span-8 flex flex-col ${conversationId ? "flex" : "hidden lg:flex"}`}>
          {active ? (
            <>
              <header className="flex items-center gap-3 p-4 md:p-5 border-b border-border bg-card sticky top-16 z-10">
                <button onClick={() => navigate("/messages")} className="lg:hidden p-2 -ml-2" data-testid="msg-back-btn"><ArrowLeft className="w-5 h-5" /></button>
                <div className="w-11 h-11 bg-muted overflow-hidden">
                  {active.artist_cover && <img src={imgSrc(active.artist_cover)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <p className="font-display font-black text-lg leading-tight">{user?.role === "client" ? active.artist_stage_name : active.client_name}</p>
                  <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{user?.role === "client" ? active.artist_category : "Client"}</p>
                </div>
                <Link to="/dashboard" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground hidden md:block">Voir le contrat →</Link>
              </header>

              {!active.unlock_paid && (
                <div className="m-5 p-4 border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 flex gap-3" data-testid="unlock-pending">
                  <ShieldAlert className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold">Paiement Mobile Money en attente</p>
                    <p className="text-xs text-muted-foreground mt-1">La messagerie s'activera dès la confirmation du paiement de 50 000 GNF.</p>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-[hsl(var(--muted))]/30" data-testid="messages-list">
                {messages.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground py-10">Démarrez la conversation 👋</p>
                ) : messages.map((m) => {
                  const mine = m.sender_id === user.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-sm border ${mine ? "bg-foreground text-background border-foreground" : "bg-card border-border"}`}>
                        <p className="text-sm whitespace-pre-line">{m.body}</p>
                        <p className={`text-[10px] font-mono mt-1 ${mine ? "text-white/60" : "text-muted-foreground"}`}>
                          {new Date(m.created_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={listEndRef} />
              </div>

              <form onSubmit={send} className="p-4 border-t border-border bg-card flex gap-2">
                <input
                  data-testid="message-input"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder={active.unlock_paid ? "Écrire un message…" : "Paiement requis pour envoyer"}
                  disabled={!active.unlock_paid || sending}
                  className="flex-1 border border-border px-4 py-2.5 rounded-sm bg-card disabled:opacity-50"
                />
                <button type="submit" disabled={!active.unlock_paid || sending || !body.trim()} data-testid="send-message-btn" className="btn-primary px-4">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
              </form>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center p-10 text-center text-muted-foreground">
              <div>
                <MessageSquare className="w-12 h-12 mx-auto mb-3" />
                <p>Sélectionnez une conversation pour démarrer.</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
